import { AfterViewInit, Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { LoadingService } from "@coso/loading";
import { DialogComponent, DialogType, IS_SAAS, setTabsManualActivation } from "@coso/utility";
import * as lodash from "lodash";
import { BehaviorSubject, forkJoin, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import {
  FolderNamingRule,
  HighAvailabilityModes,
  ILibraryDistribution,
  ISettings,
  IServiceStatusConfigurationUpdateDto,
  ISiteCollectionDistribution,
  ISiteDistribution,
  LibraryRule,
  Period,
  SiteCollectionRule,
  SiteRule,
  SubfolderNameSource,
  IServiceStatus
} from "../../models/auto-generated";
import { SettingsService } from "../../services";
import { AUserPermissionsService } from "../../services/a-user-permissions.service";
import { handleError } from "../../utils/handle-error";
import { Constants } from "./constants";
import { SharePointStructureCreatorService } from "./services/sharepoint-structure-creator.service";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { SiteSettingsComponent } from "./components/site-settings/site-settings.component";
import { SiteCollectionSettingsComponent } from "./components/site-collection-settings/site-collection-settings.component";
import { BoardComponent } from "./components/board/board.component";
import { FolderSettingsComponent } from "./components/folder-settings/folder-settings.component";
import { LibrarySettingsComponent } from "./components/library-settings/library-settings.component";
import { ScriptEditorComponent } from "./components/script-editor/script-editor.component";
import { GeneralSettingsComponent } from "./components/general-settings/general-settings.component";
import { FormBuilder, FormGroup } from "@coso/forms";

@Component({
  standalone:false,
  templateUrl: "./sharepoint-structure-creator.component.html",
  styleUrls: ["./sharepoint-structure-creator.component.scss"]
})
export class SharePointStructureCreatorComponent implements OnInit, OnDestroy, AfterViewInit {
  protected unsubscribe = new Subject<void>();

  hasChanges: boolean;
  isReadOnly: boolean;
  isReadOnlyEmmiter = new Subject<boolean>();
  errorMessage: string;
  successMessage: string;
  warningMessage: string;
  instanceId: string;
  isFailoverBackupInstance: boolean;

  rootFormGroup: FormGroup<IServiceStatusConfigurationUpdateDto>;
  private initialValue: IServiceStatusConfigurationUpdateDto;
  status: IServiceStatus;
  statusEmitter = new BehaviorSubject<IServiceStatus>(undefined);


  constructor(
    @Inject(IS_SAAS) public isSaaS: boolean,
    public loadingService: LoadingService,
    private settingsService: SettingsService,
    private userPermissionsService: AUserPermissionsService,
    private route: ActivatedRoute,
    private sscService: SharePointStructureCreatorService,
    private fb: FormBuilder,
    private modalService: NgbModal
  ) {}

  ngOnDestroy(): void {
    this.unsubscribe.next();
  }

  ngAfterViewInit(): void {
    /** This will make tabs to be compliant with WAI ARIA practices */
    setTabsManualActivation();
    /** This will set the current route tab selected. */
    this.route.firstChild.url.subscribe((segments) => {
      const segment = segments[0].path;
      const tab = document.getElementById(`tab-${segment}`);
      tab.setAttribute("aria-selected", "true");
      tab.removeAttribute("tabindex");
    });
  }

  ngOnInit(): void {
    this.instanceId = this.route.snapshot.paramMap.get("instance-id");
    forkJoin([this.userPermissionsService.hasLimitedAccess$, this.settingsService.get(this.instanceId)]).subscribe(([hasLimitedAccess, settings]) => {      
      this.isFailoverBackupInstance = this.isSaaS === false && settings.highAvailabilityMode === HighAvailabilityModes.FailoverBackup;
      this.isReadOnly = hasLimitedAccess || this.isFailoverBackupInstance;
      this.isReadOnlyEmmiter.next(this.isReadOnly);
    });
    this.generateFormGroup();
    this.loadSettings();
    this.onFormGroupChanges();
  }

  discardChanges() {
    this.errorMessage = this.successMessage = undefined;
    const modalRef = this.modalService.open(DialogComponent, {
      keyboard: false,
      ariaLabelledBy: "modalTitle"
    });
    modalRef.componentInstance.options = {
      titleText: "Confirm Discard Changes",
      type: DialogType.Warning,
      contentText: "Are you sure you want to discard all changes? This action cannot be reversed.",
      rightButtons: [
        { label: "No", type: "button", class: "btn btn-dark mr-2", onClick: () => modalRef.dismiss() },
        {
          label: "Yes",
          type: "button",
          class: "btn btn-primary",
          onClick: () => {
            this.successMessage = "All changes have been successfully discarded.";
            this.rootFormGroup.patchValue(this.initialValue);
            this.rootFormGroup.updateValueAndValidity();
            this.hasChanges = false;
            modalRef.dismiss();
          }
        }
      ],
      onClose: () => modalRef.dismiss()
    };
  }

  resetToDefault() {
    this.errorMessage = this.successMessage = undefined;
    const modalRef = this.modalService.open(DialogComponent, {
      keyboard: false,
      ariaLabelledBy: "modalTitle"
    });
    modalRef.componentInstance.options = {
      titleText: "Reset Settings to Default",
      type: DialogType.Warning,
      contentText: "Are you sure you want to restore settings to their original defaults? This action cannot be reversed.",
      rightButtons: [
        { label: "No", type: "button", class: "btn btn-dark mr-2", onClick: () => modalRef.dismiss() },
        {
          label: "Yes",
          type: "button",
          class: "btn btn-primary",
          onClick: () => {
            this.rootFormGroup.patchValue({
              settings: {
                documentLocationName: Constants.DefaultDocumentLocationPattern,
                folderNamingRule: Constants.DefaultFolderNamingRule,
                folderNamingPattern: Constants.DefaultFolderNamePattern,
                suppressLeadBehavior: Constants.DefaultSuppressLeadBehavior,
                suppressOpportunityBehavior: Constants.DefaultSuppressOpportunityBehavior,
                supportOneNote: Constants.DefaultSupportOneNote,
                breakRoleInheritance: Constants.DefaultBreakRoleInheritance,
                copyRoleAssignments: Constants.DefaultCopyRoleAssignments,
                subfolderNameSource: Constants.DefaultSubfolderNameSource,         
                skipSubfolder: Constants.DefaultSkipSubfolder,       
                customScript: ""
              },
              libraryDistribution: {
                unknownCharacter: Constants.DefaultUnknownCharacter,
                namePattern: Constants.DefaultPattern,
                characterCount: Constants.DefaultNumberOfStartingCharacters,
                libraryTemplate: Constants.DefaultLibraryTemplateId, 
                period: Constants.DefaultPeriod,               
                periodSource: Constants.DefaultPeriodSource,
                libraryTemplateName: "",
                pattern: Constants.DefaultLibraryCreateRulePattern
              },
              siteDistribution: {
                pattern: Constants.DefaultSiteRule,
                namePattern: Constants.DefaultPattern,
                webTemplate: Constants.DefaultWebTemplate,
                language: Constants.DefaultLanguage,
                period: Constants.DefaultPeriod,
                periodSource: Constants.DefaultPeriodSource,
                characterCount: Constants.DefaultNumberOfStartingCharacters,
                unknownCharacter: Constants.DefaultUnknownCharacter
              },
              siteCollectionDistribution: {
                pattern: Constants.DefaultSiteCollectionRule,
                namePattern: Constants.DefaultPattern,
                period: Constants.DefaultPeriod,
                periodSource: Constants.DefaultPeriodSource
              }
            });
            this.rootFormGroup.updateValueAndValidity();
            this.saveSettings();
            modalRef.dismiss();
          }
        }
      ],
      onClose: () => modalRef.dismiss()
    };
  }

  saveSettings(event?: SubmitEvent) {
    const button = event?.submitter as HTMLButtonElement;

    if (button && button.id === "activateTokenBtn") {
      return;
    }
    if (this.rootFormGroup.invalid) {
      this.rootFormGroup.markAllAsTouched();
      this.warningMessage = "There are items that require your attention.";
    } else {
      this.loadingService.start("Saving settings", "saveSettings");
      this.sscService.save(this.instanceId, this.rootFormGroup.getRawValue()).subscribe(
        () => {
          this.successMessage = "All changes have been successfully saved.";
          this.initialValue = lodash.cloneDeep(this.rootFormGroup.getRawValue());
          this.hasChanges = false;
          this.loadingService.stop("saveSettings");
        },
        (error) => {
          this.errorMessage = `Failed to save SharePoint Structure Creator settings due to: ${handleError(error)}`;
          this.loadingService.stop("saveSettings");
        }
      );
    }
  }

  private loadSettings() {
    this.loadingService.start("Loading SharePoint Structure Creator information", "loadInformation");
    this.sscService.get(this.instanceId).subscribe(
      (response) => {     
        this.statusEmitter.next(response);

        const initialValue = {
          settings: response.configuration.settings,
          libraryDistribution: response.configuration.libraryDistribution,
          siteDistribution: response.configuration.siteDistribution,
          siteCollectionDistribution: response.configuration.siteCollectionDistribution,          
        };
        this.rootFormGroup.patchValue(initialValue);
        this.rootFormGroup.updateValueAndValidity();
        this.initialValue = lodash.cloneDeep(initialValue);        
        this.loadingService.stop("loadInformation");
      },
      (errorResponse) => {
        this.errorMessage = `Failed to load SharePoint Structure Creator information due to: ${handleError(errorResponse)}`;
        this.loadingService.stop("loadInformation");
      }
    );
  }

  private generateFormGroup() {
    this.rootFormGroup = this.fb.group<IServiceStatusConfigurationUpdateDto>({
      settings: this.fb.group<ISettings>({
        documentLocationName: this.fb.control(Constants.DefaultDocumentLocationPattern, Validators.required),
        folderNamingRule: this.fb.control(Constants.DefaultFolderNamingRule, Validators.required),
        folderNamingPattern: this.fb.control(Constants.DefaultFolderNamePattern),
        suppressLeadBehavior: this.fb.control(Constants.DefaultSuppressLeadBehavior, Validators.required),
        suppressOpportunityBehavior: this.fb.control(Constants.DefaultSuppressOpportunityBehavior, Validators.required),
        supportOneNote: this.fb.control(Constants.DefaultSupportOneNote, Validators.required),
        breakRoleInheritance: this.fb.control(Constants.DefaultBreakRoleInheritance, Validators.required),
        copyRoleAssignments: this.fb.control(Constants.DefaultCopyRoleAssignments, Validators.required),
        subfolderNameSource: this.fb.control(Constants.DefaultSubfolderNameSource, Validators.required),
        skipSubfolder: this.fb.control(Constants.DefaultSkipSubfolder, Validators.required),
        customScript: this.fb.control("")
      }),
      libraryDistribution: this.fb.group<ILibraryDistribution>({
        pattern: this.fb.control(undefined, Validators.required),
        namePattern: this.fb.control(Constants.DefaultPattern),
        characterCount: this.fb.control(Constants.DefaultNumberOfStartingCharacters, [Validators.min(1), Validators.max(16)]),
        unknownCharacter: this.fb.control(Constants.DefaultUnknownCharacter, Validators.maxLength(1)),
        period: this.fb.control(undefined),
        periodSource: this.fb.control(Constants.DefaultPeriodSource),
        libraryTemplate: this.fb.control(Constants.DefaultLibraryTemplateId, Validators.required),
        libraryTemplateName: this.fb.control("")
      }),
      siteDistribution: this.fb.group<ISiteDistribution>({
        pattern: this.fb.control(Constants.DefaultSiteRule, Validators.required),
        period: this.fb.control(undefined),
        periodSource: this.fb.control(undefined),
        namePattern: this.fb.control(Constants.DefaultPattern),
        characterCount: this.fb.control(undefined, [Validators.min(1), Validators.max(16)]),
        unknownCharacter: this.fb.control(undefined, Validators.maxLength(1)),
        webTemplate: this.fb.control(Constants.DefaultPattern),
        language: this.fb.control(Constants.DefaultLanguage)
      }),
      siteCollectionDistribution: this.fb.group<ISiteCollectionDistribution>({
        pattern: this.fb.control(Constants.DefaultSiteCollectionRule, Validators.required),
        period: this.fb.control(undefined),
        periodSource: this.fb.control(undefined),
        namePattern: this.fb.control(Constants.DefaultPattern)
      })
    });
  }

  private onFormGroupChanges() {
    this.rootFormGroup.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((value) => {
      if (this.initialValue) {
        if (value.settings.copyRoleAssignments === undefined) {
          const clone = lodash.cloneDeep(this.initialValue);
          delete clone.settings["copyRoleAssignments"];
          this.hasChanges = lodash.isEqual(value, clone) === false;
        } else {
          this.hasChanges = lodash.isEqual(value, this.initialValue) === false;
        }
      }
    });
    this.rootFormGroup.controls.settings.controls.breakRoleInheritance.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((value) => {
      if (value) {
        this.rootFormGroup.controls.settings.controls.copyRoleAssignments.enable();
      } else {
        this.rootFormGroup.controls.settings.controls.copyRoleAssignments.disable();
      }
    });
    this.rootFormGroup.controls.settings.controls.skipSubfolder.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((value) => {
      if (value) {
        this.rootFormGroup.controls.settings.controls.subfolderNameSource.disable();
      } else {
        this.rootFormGroup.controls.settings.controls.subfolderNameSource.enable();
      }
    });
    this.rootFormGroup.controls.settings.controls.folderNamingRule.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((value) => {
      if (value === FolderNamingRule.Pattern) {
        this.rootFormGroup.controls.settings.controls.folderNamingPattern.setValidators(Validators.required);
        this.rootFormGroup.controls.settings.controls.folderNamingPattern.markAsUntouched();
        this.rootFormGroup.controls.settings.controls.folderNamingPattern.markAsPristine();
        this.rootFormGroup.controls.settings.controls.folderNamingPattern.updateValueAndValidity();
      } else {
        this.rootFormGroup.controls.settings.controls.folderNamingPattern.clearValidators();
        this.rootFormGroup.controls.settings.controls.folderNamingPattern.updateValueAndValidity();
      }
    });
    this.rootFormGroup.controls.libraryDistribution.controls.period.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((value) => {
      if (value === Period.Custom) {
        this.rootFormGroup.controls.libraryDistribution.controls.namePattern.setValidators(Validators.required);
        this.rootFormGroup.controls.libraryDistribution.controls.namePattern.markAsUntouched();
        this.rootFormGroup.controls.libraryDistribution.controls.namePattern.markAsPristine();
        this.rootFormGroup.controls.libraryDistribution.controls.namePattern.updateValueAndValidity();
      } else {
        this.rootFormGroup.controls.libraryDistribution.controls.namePattern.clearValidators();
        this.rootFormGroup.controls.libraryDistribution.controls.namePattern.updateValueAndValidity();
      }
    });
    this.rootFormGroup.controls.libraryDistribution.controls.pattern.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((value) => {
      if (value === LibraryRule.Period) {
        this.rootFormGroup.controls.libraryDistribution.controls.period.setValidators(Validators.required);
        this.rootFormGroup.controls.libraryDistribution.controls.period.markAsUntouched();
        this.rootFormGroup.controls.libraryDistribution.controls.period.markAsPristine();
        this.rootFormGroup.controls.libraryDistribution.controls.period.updateValueAndValidity();

        this.rootFormGroup.controls.libraryDistribution.controls.periodSource.setValidators(Validators.required);
        this.rootFormGroup.controls.libraryDistribution.controls.periodSource.markAsUntouched();
        this.rootFormGroup.controls.libraryDistribution.controls.periodSource.markAsPristine();
        this.rootFormGroup.controls.libraryDistribution.controls.periodSource.updateValueAndValidity();
      } else {
        this.rootFormGroup.controls.libraryDistribution.controls.period.clearValidators();
        this.rootFormGroup.controls.libraryDistribution.controls.period.updateValueAndValidity();
        this.rootFormGroup.controls.libraryDistribution.controls.periodSource.clearValidators();
        this.rootFormGroup.controls.libraryDistribution.controls.periodSource.updateValueAndValidity();
      }
      if (value === LibraryRule.StartingNameChars) {
        this.rootFormGroup.controls.libraryDistribution.controls.unknownCharacter.setValidators([Validators.required, Validators.maxLength(1)]);
        this.rootFormGroup.controls.libraryDistribution.controls.unknownCharacter.markAsUntouched();
        this.rootFormGroup.controls.libraryDistribution.controls.unknownCharacter.markAsPristine();
        this.rootFormGroup.controls.libraryDistribution.controls.unknownCharacter.updateValueAndValidity();
      } else {
        this.rootFormGroup.controls.libraryDistribution.controls.unknownCharacter.clearValidators();
        this.rootFormGroup.controls.libraryDistribution.controls.unknownCharacter.updateValueAndValidity();
      }
      if (value === LibraryRule.StartingNameChars || value === LibraryRule.StartingIdChars) {
        this.rootFormGroup.controls.libraryDistribution.controls.characterCount.setValidators([Validators.required, Validators.min(1), Validators.max(16), Validators.pattern(/^[0-9]*$/)]);
        this.rootFormGroup.controls.libraryDistribution.controls.characterCount.markAsUntouched();
        this.rootFormGroup.controls.libraryDistribution.controls.characterCount.markAsPristine();
        this.rootFormGroup.controls.libraryDistribution.controls.characterCount.updateValueAndValidity();
      } else {
        this.rootFormGroup.controls.libraryDistribution.controls.characterCount.clearValidators();
        this.rootFormGroup.controls.libraryDistribution.controls.characterCount.updateValueAndValidity();
      }
      if (value === LibraryRule.StartingNameChars || value === LibraryRule.StartingIdChars || value === LibraryRule.PerRecord || value === LibraryRule.Period) {
        this.rootFormGroup.controls.libraryDistribution.controls.namePattern.setValidators(Validators.required);
        this.rootFormGroup.controls.libraryDistribution.controls.namePattern.markAsUntouched();
        this.rootFormGroup.controls.libraryDistribution.controls.namePattern.markAsPristine();
        this.rootFormGroup.controls.libraryDistribution.controls.namePattern.updateValueAndValidity();
      } else {
        this.rootFormGroup.controls.libraryDistribution.controls.namePattern.clearValidators();
        this.rootFormGroup.controls.libraryDistribution.controls.namePattern.updateValueAndValidity();
      }
      const folderNamingRuleValue = this.rootFormGroup.controls.settings.controls.folderNamingRule.value;
      if (value === LibraryRule.PerRecord) {
        this.rootFormGroup.controls.settings.controls.folderNamingPattern.setValue(Constants.DefaultFolderNamePattern);
        this.rootFormGroup.controls.settings.controls.folderNamingPattern.setValidators(Validators.required);
        this.rootFormGroup.controls.settings.controls.folderNamingPattern.markAsUntouched();
        this.rootFormGroup.controls.settings.controls.folderNamingPattern.markAsPristine();
        this.rootFormGroup.controls.settings.controls.folderNamingPattern.updateValueAndValidity();
      } else if (folderNamingRuleValue !== FolderNamingRule.Pattern) {
        this.rootFormGroup.controls.settings.controls.folderNamingPattern.clearValidators();
        this.rootFormGroup.controls.settings.controls.folderNamingPattern.updateValueAndValidity();
      }
    });
    this.rootFormGroup.controls.siteDistribution.controls.period.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((value) => {
      if (value === Period.Custom) {
        this.rootFormGroup.controls.siteDistribution.controls.namePattern.setValidators(Validators.required);
        this.rootFormGroup.controls.siteDistribution.controls.namePattern.markAsUntouched();
        this.rootFormGroup.controls.siteDistribution.controls.namePattern.markAsPristine();
        this.rootFormGroup.controls.siteDistribution.controls.namePattern.updateValueAndValidity();
      } else {
        this.rootFormGroup.controls.siteDistribution.controls.namePattern.clearValidators();
        this.rootFormGroup.controls.siteDistribution.controls.namePattern.updateValueAndValidity();
      }
    });
    this.rootFormGroup.controls.siteDistribution.controls.pattern.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((value) => {
      if (value !== SiteRule.None) {
        this.rootFormGroup.controls.siteDistribution.controls.webTemplate.setValidators(Validators.required);
        this.rootFormGroup.controls.siteDistribution.controls.webTemplate.markAsUntouched();
        this.rootFormGroup.controls.siteDistribution.controls.webTemplate.markAsPristine();
        this.rootFormGroup.controls.siteDistribution.controls.webTemplate.updateValueAndValidity();

        this.rootFormGroup.controls.siteDistribution.controls.language.setValidators(Validators.required);
        this.rootFormGroup.controls.siteDistribution.controls.language.markAsUntouched();
        this.rootFormGroup.controls.siteDistribution.controls.language.markAsPristine();
        this.rootFormGroup.controls.siteDistribution.controls.language.updateValueAndValidity();
      } else {
        this.rootFormGroup.controls.siteDistribution.controls.webTemplate.clearValidators();
        this.rootFormGroup.controls.siteDistribution.controls.webTemplate.updateValueAndValidity();
        this.rootFormGroup.controls.siteDistribution.controls.language.clearValidators();
        this.rootFormGroup.controls.siteDistribution.controls.language.updateValueAndValidity();
      }
      if (value === SiteRule.Period) {
        this.rootFormGroup.controls.siteDistribution.controls.period.setValidators(Validators.required);
        this.rootFormGroup.controls.siteDistribution.controls.period.markAsUntouched();
        this.rootFormGroup.controls.siteDistribution.controls.period.markAsPristine();
        this.rootFormGroup.controls.siteDistribution.controls.period.updateValueAndValidity();

        this.rootFormGroup.controls.siteDistribution.controls.periodSource.setValidators(Validators.required);
        this.rootFormGroup.controls.siteDistribution.controls.periodSource.markAsUntouched();
        this.rootFormGroup.controls.siteDistribution.controls.periodSource.markAsPristine();
        this.rootFormGroup.controls.siteDistribution.controls.periodSource.updateValueAndValidity();
      } else {
        this.rootFormGroup.controls.siteDistribution.controls.period.clearValidators();
        this.rootFormGroup.controls.siteDistribution.controls.period.updateValueAndValidity();
        this.rootFormGroup.controls.siteDistribution.controls.periodSource.clearValidators();
        this.rootFormGroup.controls.siteDistribution.controls.periodSource.updateValueAndValidity();
      }
      if (value === SiteRule.StartingNameChars) {
        this.rootFormGroup.controls.siteDistribution.controls.unknownCharacter.setValidators([Validators.required, Validators.maxLength(1)]);
        this.rootFormGroup.controls.siteDistribution.controls.unknownCharacter.markAsUntouched();
        this.rootFormGroup.controls.siteDistribution.controls.unknownCharacter.markAsPristine();
        this.rootFormGroup.controls.siteDistribution.controls.unknownCharacter.updateValueAndValidity();
      } else {
        this.rootFormGroup.controls.siteDistribution.controls.unknownCharacter.clearValidators();
        this.rootFormGroup.controls.siteDistribution.controls.unknownCharacter.updateValueAndValidity();
      }
      if (value === SiteRule.StartingNameChars || value === SiteRule.StartingIdChars) {
        this.rootFormGroup.controls.siteDistribution.controls.characterCount.setValidators([Validators.required, Validators.min(1), Validators.max(16), Validators.pattern(/^[0-9]*$/)]);
        this.rootFormGroup.controls.siteDistribution.controls.characterCount.markAsUntouched();
        this.rootFormGroup.controls.siteDistribution.controls.characterCount.markAsPristine();
        this.rootFormGroup.controls.siteDistribution.controls.characterCount.updateValueAndValidity();
      } else {
        this.rootFormGroup.controls.siteDistribution.controls.characterCount.clearValidators();
        this.rootFormGroup.controls.siteDistribution.controls.characterCount.updateValueAndValidity();
      }
      if (value === SiteRule.StartingNameChars || value === SiteRule.StartingIdChars || value === SiteRule.PerRecord || value === SiteRule.Period) {
        this.rootFormGroup.controls.siteDistribution.controls.namePattern.setValidators(Validators.required);
        this.rootFormGroup.controls.siteDistribution.controls.namePattern.markAsUntouched();
        this.rootFormGroup.controls.siteDistribution.controls.namePattern.markAsPristine();
        this.rootFormGroup.controls.siteDistribution.controls.namePattern.updateValueAndValidity();
      } else {
        this.rootFormGroup.controls.siteDistribution.controls.namePattern.clearValidators();
        this.rootFormGroup.controls.siteDistribution.controls.namePattern.updateValueAndValidity();
      }
    });
    this.rootFormGroup.controls.siteCollectionDistribution.controls.period.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((value) => {
      if (value === Period.Custom) {
        this.rootFormGroup.controls.siteCollectionDistribution.controls.namePattern.setValidators(Validators.required);
        this.rootFormGroup.controls.siteCollectionDistribution.controls.namePattern.markAsUntouched();
        this.rootFormGroup.controls.siteCollectionDistribution.controls.namePattern.markAsPristine();
        this.rootFormGroup.controls.siteCollectionDistribution.controls.namePattern.updateValueAndValidity();
      } else {
        this.rootFormGroup.controls.siteCollectionDistribution.controls.namePattern.clearValidators();
        this.rootFormGroup.controls.siteCollectionDistribution.controls.namePattern.updateValueAndValidity();
      }
    });
    this.rootFormGroup.controls.siteCollectionDistribution.controls.pattern.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((value) => {
      if (value === SiteCollectionRule.Period) {
        this.rootFormGroup.controls.siteCollectionDistribution.controls.period.setValidators(Validators.required);
        this.rootFormGroup.controls.siteCollectionDistribution.controls.period.markAsUntouched();
        this.rootFormGroup.controls.siteCollectionDistribution.controls.period.markAsPristine();
        this.rootFormGroup.controls.siteCollectionDistribution.controls.period.updateValueAndValidity();

        this.rootFormGroup.controls.siteCollectionDistribution.controls.periodSource.setValidators(Validators.required);
        this.rootFormGroup.controls.siteCollectionDistribution.controls.periodSource.markAsUntouched();
        this.rootFormGroup.controls.siteCollectionDistribution.controls.periodSource.markAsPristine();
        this.rootFormGroup.controls.siteCollectionDistribution.controls.periodSource.updateValueAndValidity();
      } else {
        this.rootFormGroup.controls.siteCollectionDistribution.controls.period.clearValidators();
        this.rootFormGroup.controls.siteCollectionDistribution.controls.period.updateValueAndValidity();
        this.rootFormGroup.controls.siteCollectionDistribution.controls.periodSource.clearValidators();
        this.rootFormGroup.controls.siteCollectionDistribution.controls.periodSource.updateValueAndValidity();
      }
    });
  }

  onRouterActivated(component : BoardComponent | GeneralSettingsComponent | FolderSettingsComponent | LibrarySettingsComponent | ScriptEditorComponent | SiteSettingsComponent | SiteCollectionSettingsComponent)
  {
    var emitter: BehaviorSubject<boolean>;

    if (component instanceof BoardComponent) {
      emitter = component.isReadOnly
      let isUpdating = false;

      this.statusEmitter.subscribe((value) => {
        if (!isUpdating) {
          isUpdating = true;
          component.status.next(value);
          isUpdating = false;
        }
      }); 

      component.status.subscribe((value) => {
        if (!isUpdating) {
          isUpdating = true;
          this.statusEmitter.next(value);
          isUpdating = false;
        }
      });
    }

    if (component instanceof GeneralSettingsComponent) {  
      emitter = component.isReadOnly
    }

    if (component instanceof FolderSettingsComponent) {
      emitter = component.isReadOnly
    }

    if (component instanceof LibrarySettingsComponent) {
      emitter = component.isReadOnly
    }

    if (component instanceof ScriptEditorComponent) {
      emitter = component.isReadOnly
    }

    if (component instanceof SiteSettingsComponent) {
      emitter = component.isReadOnly
    }
    
    if (component instanceof SiteCollectionSettingsComponent) {
      emitter = component.isReadOnly
    }

    if (emitter) {
      this.isReadOnlyEmmiter.subscribe((value) => emitter.next(value));      
      this.isReadOnlyEmmiter.next(this.isReadOnly);
    }    
  }  
}
